export interface CertificateOffice {
    type: string;
    code?: string;
    name?: string;
    ar?: {
        code?: string;
        name?: string;
    };
}

export interface Operator {
    cpf: string;
    name: string;
    email: string;
    certificateOffices: CertificateOffice[];
}

export async function getNuvidioToken(operator: Operator): Promise<string> {
    // 1. Validação de Entrada do Objeto Operator
    if (!operator || typeof operator !== 'object') {
        throw new Error("Objeto 'operator' inválido fornecido para gerar token NuVidio.");
    }
    const { cpf, name, email, certificateOffices } = operator;

    if (!cpf || typeof cpf !== 'string' || cpf.trim() === '') {
        throw new Error("CPF do operador inválido ou ausente.");
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
        throw new Error("Nome do operador inválido ou ausente.");
    }
    if (!email || typeof email !== 'string' || email.trim() === '') {
        throw new Error("E-mail do operador inválido ou ausente.");
    }
    if (!certificateOffices || !Array.isArray(certificateOffices)) {
        throw new Error("Lista de escritórios de certificado do operador inválida ou ausente.");
    }

    // Verifica se o e-mail possui um domínio válido
    if (!email.includes("@")) {
        throw new Error("E-mail do operador inválido: formato incorreto (domínio ausente).");
    }

    const [, domain] = email.split("@");
    if (!domain || domain.trim() === '') {
        throw new Error("Domínio do operador não encontrado no e-mail.");
    }

    // Gera novo e-mail com CPF no domínio original
    const formattedEmail = `${cpf}@${domain}`;

    // Filtra escritórios virtuais válidos
    const virtualOffices = certificateOffices.filter(
        (office) => office.type === "Virtual"
    );

    if (!virtualOffices.length) {
        throw new Error("Nenhum escritório virtual encontrado para este operador. O token NuVidio requer ao menos um escritório virtual.");
    }

    try {
        const response = await fetch("/api/nuvidio/token", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                cpf,
                name,
                email: formattedEmail,
                certificateOffices: virtualOffices,
            }),
        });

        let jsonResponse: any = {};
        // 2. Tratamento Robusto de JSON
        try {
            jsonResponse = await response.json();
        } catch (parseError) {
            console.error("[NuVidio Token] Erro ao parsear JSON da resposta:", parseError);
            if (response.ok) {
                throw new Error("Resposta inválida do servidor: esperado JSON, mas recebeu outro formato ao gerar token NuVidio.");
            }
            // Se não foi OK, o erro será capturado no `if (!response.ok)` abaixo.
        }

        // 3. Tratamento de Respostas Não-OK (HTTP status codes 4xx, 5xx) ou token ausente
        if (!response.ok) {
            const detail = jsonResponse?.error || jsonResponse?.detail || jsonResponse?.message || response.statusText || 'Erro desconhecido';
            const statusCode = response.status;
            throw new Error(`Falha na API ao gerar token NuVidio (${statusCode}): ${detail}`);
        }

        const token = jsonResponse.token;

        if (typeof token !== 'string' || !token || token.trim() === '') {
            throw new Error("Token NuVidio não retornado ou inválido na resposta do backend.");
        }

        return token;
    } catch (err: any) {
        // Log do erro completo para depuração, relançando para o chamador.
        console.error("[NuVidio Token] Erro durante a solicitação do token:", err.message || err);
        throw err; // Relança o erro original ou uma nova instância se for um tipo genérico.
    }
}